import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { PAYMENT_METHODS } from "@sakani/contracts";
import { ApiError, api, savedBookings, shrinkImage, uploadImage, uploadProof, type BookingDetail } from "../api.ts";
import { useAuth } from "../auth.tsx";
import { money } from "../format.ts";
import { pick, useI18n } from "../i18n.tsx";
import { AuthForm } from "../ui/AuthForm.tsx";
import { IconArrow } from "../ui/Icons.tsx";
import { Pattern } from "../ui/Pattern.tsx";

/** How far along the managed flow a status is: request → availability → payment → keys. */
const PROGRESS: Record<string, number> = { requested: 1, awaiting_payment: 2, confirmed: 3, in_stay: 4, completed: 4 };
const DEAD = ["declined", "expired", "cancelled_by_guest", "cancelled_by_host"];

/**
 * One page, two doors. `link`: a guest without an account, authorised by the token in the link.
 * `account`: a signed-in guest opening one of their own stays. Same screen, same tracking.
 */
export function BookingPage({ mode }: { mode: "link" | "account" }) {
  const { id = "" } = useParams();
  const { hash } = useLocation();
  const nav = useNavigate();
  const { t, locale } = useI18n();
  const { me, refresh } = useAuth();
  // The link carries the token in its fragment; a returning guest on the same device falls back to what we saved.
  const token = mode === "link" ? hash.slice(1) || savedBookings().find((b) => b.id === id)?.token || "" : "";
  const [d, setD] = useState<BookingDetail | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "invalid" | "error">("loading");
  const [pay, setPay] = useState({ method: "bankily", reference: "" });
  const [sent, setSent] = useState(false);
  const [shot, setShot] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [payErr, setPayErr] = useState<string | null>(null);
  const [keepOpen, setKeepOpen] = useState(false);
  const [kept, setKept] = useState(false);
  useEffect(() => {
    if (!shot) { setPreview(null); return; }
    const u = URL.createObjectURL(shot);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [shot]);

  const base = mode === "link" ? `/guest/bookings/${id}` : `/account/bookings/${id}`;
  const load = useCallback(async () => {
    try { setD(await api<BookingDetail>(base, { token })); setState("ok"); }
    catch (e) { setState((e as { status?: number }).status === 401 || (e as { status?: number }).status === 404 ? "invalid" : "error"); }
  }, [base, token]);
  useEffect(() => { if (mode === "account" && me === undefined) return; void load(); }, [load, mode, me]);
  // Staff answer in minutes to hours: refresh while the guest keeps the page open.
  useEffect(() => { const i = setInterval(() => void load(), 30_000); return () => clearInterval(i); }, [load]);

  if (state === "loading") return <div className="wrap section"><div className="skel" style={{ aspectRatio: "16/7" }} aria-hidden="true" /></div>;
  if (state !== "ok" || !d) return <div className="wrap section"><div className="note"><Pattern /><p className="h2">{state === "invalid" ? t("link.invalid") : t("error")}</p><Link className="link" to="/">{t("back")}</Link></div></div>;

  const b = d.booking;
  const step = PROGRESS[b.status] ?? 0;
  const dead = DEAD.includes(b.status);
  const due = d.payments.find((p) => p.kind === "deposit" && p.status === "due");
  const cancellable = ["requested", "awaiting_payment", "confirmed"].includes(b.status);
  const title = pick(d.listing.title, locale);
  const fmt = (iso: string) => new Date(iso).toLocaleString(locale === "ar" ? "ar-MA-u-nu-latn" : locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  async function submitProof(e: React.FormEvent) {
    e.preventDefault();
    if (!due || (!pay.reference.trim() && !shot)) return;
    setSending(true); setPayErr(null);
    try {
      const blob = shot ? await shrinkImage(shot) : undefined;
      const proofKey = blob ? (mode === "link" ? await uploadProof(id, token, blob) : await uploadImage("proof", blob)) : undefined;
      const body = { method: pay.method, reference: pay.reference.trim() || undefined, proofKey };
      await api(mode === "link" ? `/guest/bookings/${id}/payments/${due.id}/submit` : `/account/payments/${due.id}/submit`, { token, body });
      setSent(true);
      void load();
    } catch (err) {
      const k = err instanceof ApiError ? `err.${err.code}` : "";
      setPayErr(k && t(k) !== k ? t(k) : t("error"));
    } finally { setSending(false); }
  }
  async function cancel() {
    await api(`${base}/cancel`, { token, method: "POST", body: {} });
    void load();
  }
  async function keep() {
    await api("/account/claim", { body: { bookingId: id, token } });
    setKept(true);
    nav(`/stays/${id}`, { replace: true });
  }

  // The journey the guest follows: each milestone with the moment it happened.
  const journey: { key: string; at: string | null }[] = [
    { key: "j.requested", at: b.createdAt },
    { key: "j.accepted", at: b.acceptedAt },
    { key: "j.confirmed", at: b.confirmedAt },
    { key: "j.keys", at: b.checkedInAt },
    { key: "j.completed", at: b.completedAt },
  ];
  const next = journey.findIndex((j) => !j.at);

  return (
    <div className="wrap section">
      {mode === "account" && <Link to="/account" className="link small">{t("my.back")}</Link>}
      <span className="eyebrow" style={{ display: "block", marginTop: mode === "account" ? 16 : 0 }}>{title} · <span className="num">{b.code}</span></span>
      <h1 className="h1" style={{ margin: "12px 0 24px" }}>{t(`status.${b.status}`)}</h1>
      <span className={`status-line ${dead || step >= 3 ? "done" : ""}`}><i /><span className="num" dir="ltr">{b.checkIn} → {b.checkOut}</span></span>

      {!dead && (
        <ol className="timeline" aria-label="progress">
          {[1, 2, 3, 4].map((n) => <li key={n} className={n < step ? "done" : n === step ? "now" : ""}>{t(`tl.${n}`)}</li>)}
        </ol>
      )}

      <div className="stack" style={{ marginBlockStart: dead ? 40 : 0 }}>
        <div>
          <div className="kv"><span className="muted">{t("b.dates")}</span><span className="num" dir="ltr">{b.nights} · {b.checkIn} → {b.checkOut}</span></div>
          <div className="kv"><span className="muted">{t("b.total")}</span><b className="num" style={{ fontWeight: 400 }}>{money(b.totalMinor, b.currency, locale)}</b></div>
        </div>

        {due && b.status === "awaiting_payment" && (
          <form className="panel" onSubmit={submitProof}>
            <div className="price-line"><span className="eyebrow" style={{ flex: 1 }}>{t("b.pay")}</span><b className="num">{money(due.amountMinor, b.currency, locale)}</b></div>
            <label className="field"><span>{t("b.method")}</span>
              <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
                {PAYMENT_METHODS.filter((m) => m !== "card").map((m) => <option key={m} value={m}>{t(`m.${m}`)}</option>)}
              </select>
            </label>
            <p className="muted small" style={{ margin: 0 }}>{t("b.proofRule")}</p>
            <label className="field"><span>{t("b.reference")}</span><input autoComplete="off" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></label>
            <div className="or"><span className="eyebrow">{t("b.or")}</span></div>
            {preview ? (
              <div className="shot">
                <img src={preview} alt="" />
                <div><b className="small">{t("b.attached")}</b><button type="button" className="link small" onClick={() => setShot(null)}>{t("b.remove")}</button></div>
              </div>
            ) : (
              <label className="attach"><input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setShot(e.target.files?.[0] ?? null)} />{t("b.attach")}</label>
            )}
            {payErr && <p className="err" role="alert">{payErr}</p>}
            <button className="btn" type="submit" disabled={sending || (!pay.reference.trim() && !shot)}>{sending ? t("req.sending") : t("b.sendProof")} <IconArrow /></button>
            {sent && <p className="muted small" role="status">{t("b.proofSent")}</p>}
          </form>
        )}

        {!dead && (
          <section aria-label={t("j.title")}>
            <div className="section-head" style={{ marginBlockEnd: 8, paddingBlockEnd: 12 }}><h2 className="h2">{t("j.title")}</h2><span className="muted small">{t("j.updated")}</span></div>
            <ol className="journey">
              {journey.map((j, i) => (
                <li key={j.key} className={j.at ? "done" : i === next ? "now" : ""}>
                  <span>{t(j.key)}</span>
                  <span className="muted small num">{j.at ? fmt(j.at) : i === next ? "…" : t("j.waiting")}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {(b.status === "confirmed" || b.status === "in_stay") && (
          <section className="panel" aria-label={t("s.title")}>
            <h2 className="h2">{t("s.title")}</h2>
            {d.listing.address && <div className="kv"><span className="muted">{t("s.address")}</span><span dir="auto">{d.listing.address}</span></div>}
            <div className="kv"><span className="muted">{t("s.checkin")}</span><span className="num">{b.checkIn} · {d.listing.checkInTime}</span></div>
            <div className="kv"><span className="muted">{t("s.checkout")}</span><span className="num">{b.checkOut} · {d.listing.checkOutTime}</span></div>
            <p className="muted small">{b.status === "confirmed" ? t("s.keysHow") : t("s.inStay")}</p>
          </section>
        )}
        {d.support.whatsapp && !dead && b.status !== "completed" && (
          <a className="btn ghost" style={{ justifySelf: "start", textDecoration: "none" }} target="_blank" rel="noreferrer" href={`https://wa.me/${d.support.whatsapp}?text=${encodeURIComponent(`${t("s.helpMsg")} ${b.code}`)}`}>{t("s.help")}</a>
        )}

        {b.status === "completed" && <Review mode={mode} bookingId={id} signedIn={Boolean(me)} />}
        {dead && <p className="muted">{t("s.cancelled")}</p>}

        {mode === "link" && !kept && (
          <section className="panel" aria-label={t("keep.title")}>
            <h2 className="h2">{t("keep.title")}</h2>
            <p className="muted small">{t("keep.lead")}</p>
            {me ? <button className="btn ghost" onClick={() => void keep()}>{t("keep.add")}</button>
              : keepOpen ? <AuthForm onDone={async () => { await refresh(); await keep(); }} />
              : <button className="btn ghost" onClick={() => setKeepOpen(true)}>{t("keep.open")}</button>}
          </section>
        )}

        {mode === "link" && <p className="muted small">{t("b.save")}</p>}
        {cancellable && <button className="btn ghost" style={{ justifySelf: "start" }} onClick={cancel}>{t("b.cancel")}</button>}
      </div>
    </div>
  );
}

function Review({ mode, bookingId, signedIn }: { mode: "link" | "account"; bookingId: string; signedIn: boolean }) {
  const { t } = useI18n();
  const [overall, setOverall] = useState(0);
  const [comment, setComment] = useState("");
  const [state, setState] = useState<"idle" | "sent" | "busy">("idle");
  const [err, setErr] = useState<string | null>(null);
  if (mode === "link" || !signedIn) return <div className="panel"><h2 className="h2">{t("s.after")}</h2><p className="muted small">{t("r.needAccount")}</p></div>;
  if (state === "sent") return <div className="panel"><h2 className="h2">{t("s.after")}</h2><p role="status">{t("r.sent")}</p></div>;
  return (
    <form className="panel" onSubmit={async (e) => {
      e.preventDefault(); setState("busy"); setErr(null);
      try { await api(`/account/bookings/${bookingId}/review`, { body: { overall, comment: comment.trim() || undefined } }); setState("sent"); }
      catch (x) { const k = x instanceof ApiError ? `err.${x.code}` : ""; setErr(k && t(k) !== k ? t(k) : t("error")); setState("idle"); }
    }}>
      <h2 className="h2">{t("s.after")}</h2>
      <span className="eyebrow">{t("r.overall")}</span>
      <div className="stars" role="radiogroup" aria-label={t("r.overall")}>
        {[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" role="radio" aria-checked={overall === n} className={n <= overall ? "on" : ""} onClick={() => setOverall(n)}>{n}</button>)}
      </div>
      <label className="field"><span>{t("r.comment")}</span><textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} /></label>
      {err && <p className="err" role="alert">{err}</p>}
      <button className="btn" type="submit" disabled={!overall || state === "busy"}>{t("r.send")}</button>
    </form>
  );
}
